import React, { useState, useEffect } from "react";
import axios from "axios";
import { formatDate } from "@telerik/kendo-intl";
import { toast } from "react-toastify";
import { BASE_URL } from "../../constants";
import KendoLoader from "../KendoLoader";

const AuditHistory = ({ entityId, entityType = "entity" }) => {
    const [auditData, setAuditData] = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    const [userNames, setUserNames] = useState({});

    const fetchAuditData = async () => {
        if (!entityId || entityId === "new") return;

        setAuditLoading(true);
        try {
            const res = await axios.get(`${BASE_URL}/api/Audit/entity/${entityId}`);
            const auditRecords = res.data || [];
            setAuditData(auditRecords);

            if (auditRecords.length > 0) {
                await fetchUserDisplayNames(auditRecords);
            }
        } catch (error) {
            console.error("Error fetching audit data:", error);
            toast.error("Failed to load audit data");
        } finally {
            setAuditLoading(false);
        }
    };

    const fetchUserDisplayNames = async (auditRecords) => {
        try {
            const uniqueUserIds = [...new Set(auditRecords.map(audit => audit.changedBy))];
            const newUserNames = { ...userNames };
            const usersToFetch = uniqueUserIds.filter(userId => !newUserNames[userId]);

            if (usersToFetch.length === 0) return;

            try {
                const response = await axios.get(`${BASE_URL}/UserAccount?searchText=~ALL&page=0&pageSize=1000`);
                const allUsers = response.data.data || [];

                usersToFetch.forEach(userId => {
                    const user = allUsers.find(u => u.id === userId);
                    if (user) {
                        newUserNames[userId] = user.displayname || user.username || userId;
                    } else {
                        newUserNames[userId] = userId;
                    }
                });

                setUserNames(newUserNames);
            } catch (error) {
                console.error('Error fetching user data:', error);
                usersToFetch.forEach(userId => {
                    newUserNames[userId] = userId;
                });
                setUserNames(newUserNames);
            }
        } catch (error) {
            console.error('Error fetching user display names:', error);
        }
    };

    const formatAuditType = (action) => {
        switch (action) {
            case 'Insert':
                return 'Created';
            case 'Update':
                return 'Modified';
            case 'Delete':
                return 'Deleted';
            case 'Print':
                return 'Printed';
            default:
                return action;
        }
    };

    const getAuditDetails = (action, oldValues, newValues) => {
        if (action === 'Insert') {
            return { type: 'creation', message: `${entityType === 'job' ? 'Job' : 'PO'} was created`, changes: [] };
        }

        if (action === 'Print' && newValues) {
            try {
                const printData = JSON.parse(newValues);
                const documentName = printData.DocumentName || 'Document';
                const userDisplayName = printData.UserDisplayName || 'Unknown User';
                return { 
                    type: 'print', 
                    message: `${documentName}`, 
                    changes: [] 
                };
            } catch (e) {
                return { type: 'print', message: 'Document was printed', changes: [] };
            }
        }

        if (action === 'Update' && oldValues && newValues) {
            try {
                const old = JSON.parse(oldValues);
                const newVal = JSON.parse(newValues);
                const changes = [];

                Object.keys(newVal).forEach(key => {
                    if (old[key] !== newVal[key] && key !== 'ModificationDate') {
                        // Skip changes where both old and new values are effectively empty
                        const oldVal = old[key];
                        const newVal_key = newVal[key];
                        const isOldEmpty = oldVal === null || oldVal === undefined || oldVal === '';
                        const isNewEmpty = newVal_key === null || newVal_key === undefined || newVal_key === '';

                        // Skip if both values are empty or if it's just changing from null to empty string or vice versa
                        if (isOldEmpty && isNewEmpty) {
                            return;
                        }

                        // Include the change if at least one value has meaningful content
                        changes.push({
                            field: formatFieldName(key),
                            oldValue: formatFieldValue(key, oldVal),
                            newValue: formatFieldValue(key, newVal_key)
                        });
                    }
                });

                if (changes.length > 0) {
                    return {
                        type: 'update',
                        message: `Updated ${changes.length} field${changes.length > 1 ? 's' : ''}`,
                        changes
                    };
                } else {
                    // If no meaningful changes were found, return a generic update message with no details
                    return {
                        type: 'update',
                        message: 'Record was updated (no significant field changes)',
                        changes: []
                    };
                }
            } catch (e) {
                return { type: 'update', message: 'Data was updated', changes: [] };
            }
        }

        return { type: 'other', message: `${entityType === 'job' ? 'Job' : 'PO'} was ${action.toLowerCase()}`, changes: [] };
    };

    const formatFieldName = (fieldName) => {
        const fieldMappings = {
            // Common fields
            'ReceivedDate': 'Received Date',
            'TotalValue': 'Total Value',
            'Commodity': 'Commodity',
            'Remarks': 'Remarks',
            'Status': 'Status',

            // PO specific fields
            'PoNo': 'PO Number',
            'VesselId': 'Vessel',
            'CustomerId': 'Customer',
            'SupplierId': 'Supplier',
            'CurrencyCode': 'Currency',
            'CargoType': 'Cargo Type',

            // Job specific fields
            'JobNo': 'Job Number',
            'JobStatus': 'Job Status',
            'JobType': 'Job Type',
            'ETD': 'ETD',
            'ETA': 'ETA',
            'OriginPort': 'Origin Port',
            'DestinationPort': 'Destination Port',
            'AirLine': 'Airline',
            'Awb': 'AWB Number',
            'Carrier': 'Carrier',
            'CollectionAddress': 'Collection Address',
            'ShipperAddress': 'Shipper Address',
            'ConsigneeAddress': 'Consignee Address'
        };
        return fieldMappings[fieldName] || fieldName;
    };

    const formatFieldValue = (fieldName, value) => {
        if (value === null || value === undefined || value === '') return 'Not set';

        if ((fieldName === 'ReceivedDate' || fieldName === 'ETD' || fieldName === 'ETA') && value) {
            return formatDate(new Date(value), "MMM dd, yyyy");
        }

        if (fieldName === 'TotalValue' && typeof value === 'number') {
            return value.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD'
            });
        }

        return value.toString();
    };

    useEffect(() => {
        fetchAuditData();
    }, [entityId]);

    // Expose refresh function to parent components
    useEffect(() => {
        if (window.refreshAuditData) {
            window.refreshAuditData = fetchAuditData;
        }
    }, []);

    return (
        <div className="bg-white rounded-lg shadow-sm border">
            {auditLoading ? (
                <div className="flex justify-center items-center p-8">
                    <KendoLoader />
                </div>
            ) : (
                <div className="max-h-[600px] overflow-y-auto">
                    {auditData.length === 0 ? (
                        <div className="p-6 text-center">
                            <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="text-base font-medium text-gray-900 mb-1">No Audit History</h3>
                            <p className="text-sm text-gray-500">No changes recorded for this {entityType === 'job' ? 'job' : 'PO'}.</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {auditData.map((audit, index) => {
                                const auditType = formatAuditType(audit.action);
                                const auditDetails = getAuditDetails(audit.action, audit.oldValues, audit.newValues);
                                const isLatest = index === 0;

                                return (
                                    <div key={audit.id} className={`p-4 ${isLatest ? 'bg-slate-50' : 'hover:bg-gray-50'} transition-colors duration-150`}>
                                        <div className="flex items-start space-x-3">
                                            {/* Timeline dot */}
                                            <div className="flex-shrink-0 relative">
                                                <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-slate-100 border border-slate-200">
                                                    {auditType === 'Created' ? (
                                                        <svg className="w-3 h-3 text-slate-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                        </svg>
                                                    ) : auditType === 'Modified' ? (
                                                        <svg className="w-3 h-3 text-slate-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                        </svg>
                                                    ) : auditType === 'Deleted' ? (
                                                        <svg className="w-3 h-3 text-slate-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    ) : auditType === 'Printed' ? (
                                                        <svg className="w-3 h-3 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                                        </svg>
                                                    ) : (
                                                        <span className="text-xs font-medium text-slate-600">{auditType[0]}</span>
                                                    )}
                                                </span>
                                                {index !== auditData.length - 1 && (
                                                    <div className="absolute top-6 left-1/2 transform -translate-x-1/2 w-px h-6 bg-slate-200"></div>
                                                )}
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center space-x-2">
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                                                            {auditType}
                                                        </span>
                                                        {isLatest && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                                                Latest
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    <div className="text-right text-xs">
                                                        <div className="font-medium text-gray-900">
                                                            {userNames[audit.changedBy] || audit.changedBy}
                                                        </div>
                                                        <div className="text-gray-500">
                                                            {formatDate(new Date(audit.changedAt), "MMM dd, HH:mm")}
                                                        </div>
                                                    </div>
                                                </div>

                                                <p className="text-sm text-gray-700 mb-2">{auditDetails.message}</p>

                                                {/* Changes comparison */}
                                                {auditDetails.changes && auditDetails.changes.length > 0 && (
                                                    <div className="space-y-2">
                                                        {auditDetails.changes.map((change, changeIndex) => (
                                                            <div key={changeIndex} className="bg-gray-50 rounded p-3">
                                                                <div className="text-xs font-medium text-gray-800 mb-2">{change.field}</div>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                                    {/* Old Value */}
                                                                    <div>
                                                                        <div className="flex items-center space-x-1 mb-1">
                                                                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                                                            <span className="text-xs text-gray-600">Previous</span>
                                                                        </div>
                                                                        <div className="bg-white rounded p-2 shadow-sm">
                                                                            <span className="text-xs text-gray-700 font-mono break-words">{change.oldValue}</span>
                                                                        </div>
                                                                    </div>

                                                                    {/* New Value */}
                                                                    <div>
                                                                        <div className="flex items-center space-x-1 mb-1">
                                                                            <div className="w-1.5 h-1.5 bg-slate-600 rounded-full"></div>
                                                                            <span className="text-xs text-gray-600">Current</span>
                                                                        </div>
                                                                        <div className="bg-white rounded p-2 shadow-sm border-l-2 border-slate-300">
                                                                            <span className="text-xs text-gray-900 font-mono break-words font-medium">{change.newValue}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AuditHistory;
